import React, { useState } from 'react';
import { useApp } from '../../../context/AppContext';
import API from '../../../utils/api';
import { Card, CardHeader, CardTitle, Btn, Badge, Chip, SectionHead, Table, Tr, Td, Modal, FormGroup, FormGrid, Input, CustomSelect } from '../../common/UI';
import { getFlag } from '../../../utils/countryFlags';

export default function AdminSetup({ section = 'customers' }) {
  const {
    customers, addCustomer, updateCustomer, deleteCustomer,
    vessels, addVessel, updateVessel, deleteVessel,
    suppliers, addSupplier, updateSupplier, deleteSupplier,
    locations, addLocation, deleteLocation, updateLocation,
    freightTypes, addFreight, deleteFreight, updateFreight,
    countries, addCountry, deleteCountry, updateCountry,
    users, updateAdminUser, deleteUser,
    showToast
  } = useApp();

  const tab = section;
  const [modal, setModal] = useState(null); // { type, mode, item? }
  const [formData, setFD] = useState({});
  const [search, setSearch] = useState('');
  const [vesselView, setVesselView] = useState('grid');
  const [confirmDelete, setConfirmDelete] = useState(null); // { type, id, name }
  const [deleteError, setDeleteError] = useState('');
  const [formError, setFormError] = useState('');

  const [custPage, setCustPage] = useState(1);
  const [suppPage, setSuppPage] = useState(1);
  const [vesselPage, setVesselPage] = useState(1);
  const itemsPerPage = 9;

  const [showPass, setShowPass] = useState(false);
  const inputBase = { background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '8px 11px', fontSize: 13, color: 'var(--text)', fontFamily: 'inherit', outline: 'none', width: '100%' };

  const openAdd = (type, defaults = {}) => { setModal({ type, mode: 'add' }); setFD(defaults); setFormError(''); };
  const openEdit = (type, item) => { setModal({ type, mode: 'edit' }); setFD({ ...item }); setFormError(''); };
  const closeM = () => { setModal(null); setFD({}); setFormError(''); };
  const set = (k, v) => setFD(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setFormError('');
    const { type, mode } = modal;
    try {
      if (type === 'customer') {
        if (!formData.company || !formData.email) { setFormError('Fill required fields'); return; }
        if (mode === 'add') {
          if (!formData.password || formData.password !== formData.confirmPassword) {
            setFormError('Passwords must match and be provided');
            return;
          }
          await addCustomer(formData);
        } else {
          await updateCustomer(formData.id, formData);
        }
      } else if (type === 'vessel') {
        if (!formData.name || !formData.imo) { setFormError('Fill required fields'); return; }
        mode === 'add' ? await addVessel(formData) : await updateVessel(formData.id, formData);
      } else if (type === 'supplier') {
        if (!formData.name) { setFormError('Fill required fields'); return; }
        mode === 'add' ? await addSupplier(formData) : await updateSupplier(formData.id, formData);
      } else if (type === 'location') {
        if (!formData.code || !formData.name) { setFormError('Fill required fields'); return; }
        mode === 'add' 
          ? await addLocation({ code: formData.code, name: formData.name, note: formData.note || '' })
          : await updateLocation(formData.id, { code: formData.code, name: formData.name, note: formData.note || '' });
      } else if (type === 'freight') {
        if (!formData.name) { setFormError('Fill required fields'); return; }
        mode === 'add'
          ? await addFreight({ name: formData.name, mode: formData.mode || '', code: formData.code || '' })
          : await updateFreight(formData.id, { name: formData.name, mode: formData.mode || '', code: formData.code || '' });
      } else if (type === 'country') {
        if (!formData.name) { setFormError('Fill required fields'); return; }
        mode === 'add'
          ? await addCountry({ name: formData.name, code: formData.code || '', flag: formData.flag || '🏳' })
          : await updateCountry(formData.id, { name: formData.name, code: formData.code || '', flag: formData.flag || '🏳' });
      } else if (type === 'user') {
        if (!formData.name || !formData.email || !formData.role) { setFormError('Fill required fields'); return; }
        if (mode === 'add') {
          if (!formData.password) { setFormError('Password is required for new users'); return; }
          await API.post('/admin/users', formData);
          showToast('User created successfully', 'success');
        } else {
          await updateAdminUser(formData.id, formData);
        }
        closeM();
        return;
      }
      closeM();
    } catch (err) {
      setFormError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || `Error saving ${type}. Re-check your data, it might already exist.`);
    }
  };
  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleteError('');
    const { type, id } = confirmDelete;
    try {
      if (type === 'customer') await deleteCustomer(id);
      else if (type === 'vessel') await deleteVessel(id);
      else if (type === 'supplier') await deleteSupplier(id);
      else if (type === 'location') await deleteLocation(id);
      else if (type === 'freight') await deleteFreight(id);
      else if (type === 'country') await deleteCountry(id);
      else if (type === 'user') await API.delete(`/admin/users/${id}`);
      
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(`This ${type} cannot be deleted because it is currently linked to ongoing stock, active delivery entries, or user accounts. Please clear those usages first.`);
    }
  };
  const sectionTitles = {
    customers: 'Companies',
    vessels: 'Vessels',
    suppliers: 'Suppliers',
    masters: 'Configurations',
  };

  const modalTitles = { customer: 'Company Profile', vessel: 'Vessel', supplier: 'Supplier', location: 'Warehouse Location', freight: 'Freight Type', country: 'Origin Country' };

  return (
    <div className="fade-up" style={{ padding: 24 }}>
      <SectionHead title={sectionTitles[tab]} subtitle={`Manage ${sectionTitles[tab].toLowerCase()} database`} />

      {/* ── CUSTOMERS ── */}
      {tab === 'customers' && (() => {
        const filtered = [...customers].reverse().filter(c =>
          !search ||
          c.company.toLowerCase().includes(search.toLowerCase()) ||
          c.email.toLowerCase().includes(search.toLowerCase()) ||
          c.contact.toLowerCase().includes(search.toLowerCase())
        );
        const totalPages = Math.ceil(filtered.length / itemsPerPage);
        const currentItems = filtered.slice((custPage - 1) * itemsPerPage, custPage * itemsPerPage);

        return (
          <Card>
            <CardHeader>
              <CardTitle>Registered Companies ({filtered.length})</CardTitle>
              <div style={{ display: 'flex', gap: 12, flex: 1, marginLeft: 20 }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
                  <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }} width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search company, contact, email..." style={{ ...inputBase, paddingLeft: 32 }} />
                </div>
              </div>
              <Btn variant="primary" size="sm" onClick={() => openAdd('customer', { status: 'active' })}>+ Add Company</Btn>
            </CardHeader>
            <Table headers={['Company', 'Contact', 'Email', 'Phone', 'Active Stock', 'Completed', 'Status', 'Actions']}>
              {currentItems.map(c => (
                <Tr key={c.id}>
                  <Td strong>{c.company}</Td>
                  <Td>{c.contact}</Td>
                  <Td>{c.email}</Td>
                  <Td>{c.phone || '—'}</Td>
                  <Td><span style={{ color: 'var(--success)', fontWeight: 700 }}>{c.activeStock}</span></Td>
                  <Td>{c.completed}</Td>
                  <Td><Badge status={c.status} /></Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn size="xs" variant="outline" onClick={() => openEdit('customer', c)}>Edit</Btn>
                      <Btn size="xs" variant="danger" onClick={() => setConfirmDelete({ type: 'customer', id: c.id, name: c.company })}>Delete</Btn>
                    </div>
                  </Td>
                </Tr>
              ))}
            </Table>
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid var(--border)', background: 'var(--white)' }}>
                    <Btn variant="outline" size="sm" onClick={() => setCustPage(p => Math.max(1, p - 1))} disabled={custPage === 1}>Previous</Btn>
                    
                    <div style={{ display: 'flex', gap: 6 }}>
                        {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(page => (
                            <button key={page} onClick={() => setCustPage(page)}
                                style={{
                                    width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                                    background: custPage === page ? 'var(--accent)' : 'transparent',
                                    color: custPage === page ? '#fff' : 'var(--text2)',
                                    transition: 'all .15s'
                                }}>
                                {page}
                            </button>
                        ))}
                    </div>
                    
                    <Btn variant="outline" size="sm" onClick={() => setCustPage(p => Math.min(totalPages, p + 1))} disabled={custPage === totalPages}>Next</Btn>
                </div>
            )}
          </Card>
        )
      })()}

      {/* ── VESSELS ── */}
      {tab === 'vessels' && (() => {
        const filtered = [...vessels].reverse().filter(v =>
          !search ||
          v.name.toLowerCase().includes(search.toLowerCase()) ||
          v.imo.includes(search)
        );
        const totalPages = Math.ceil(filtered.length / itemsPerPage);
        const currentItems = filtered.slice((vesselPage - 1) * itemsPerPage, vesselPage * itemsPerPage);
        return (
          <Card>
            <CardHeader>
              <CardTitle>Registered Vessels ({filtered.length})</CardTitle>
              <div style={{ display: 'flex', gap: 12, flex: 1, marginLeft: 20 }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
                  <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }} width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vessel name, IMO..." style={{ ...inputBase, paddingLeft: 32, width: '100%' }} />
                </div>
              </div>
              <div style={{ display: 'flex', background: 'var(--surface2)', borderRadius: 8, padding: 3 }}>
                <button onClick={() => setVesselView('grid')} style={{ border: 'none', background: vesselView === 'grid' ? 'var(--white)' : 'transparent', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', boxShadow: vesselView === 'grid' ? 'var(--shadow)' : 'none', fontSize: 13, fontWeight: 600, color: vesselView === 'grid' ? 'var(--text)' : 'var(--muted)' }}>Grid</button>
                <button onClick={() => setVesselView('list')} style={{ border: 'none', background: vesselView === 'list' ? 'var(--white)' : 'transparent', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', boxShadow: vesselView === 'list' ? 'var(--shadow)' : 'none', fontSize: 13, fontWeight: 600, color: vesselView === 'list' ? 'var(--text)' : 'var(--muted)' }}>List</button>
              </div>
              <Btn variant="primary" size="sm" onClick={() => openAdd('vessel', { status: 'active' })}>+ Add Vessel</Btn>
            </CardHeader>
            {vesselView === 'grid' ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, padding: 16 }}>
                {currentItems.map(v => (
                  <div key={v.id}
                    style={{ border: '1.5px solid var(--border)', borderRadius: 10, padding: 16, background: 'var(--white)', transition: 'all .2s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = ''; }}>
                    <div style={{ fontSize: 26, marginBottom: 10 }}>🚢</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{v.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', margin: '2px 0 10px', fontFamily: "'JetBrains Mono',monospace" }}>
                      IMO {v.imo} · {v.type}
                    </div>
                    <div style={{ display: 'flex', gap: 14, marginBottom: 12 }}>
                      {[['Active', v.activeStock, 'var(--success)'], ['Pending', v.pending, 'var(--warning)'], ['Done', v.completed, 'var(--info)']].map(([l, val, c]) => (
                        <div key={l}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: c }}>{val}</div>
                          <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)' }}>{l}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn size="xs" variant="outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => openEdit('vessel', v)}>Edit</Btn>
                      <Btn size="xs" variant="danger" onClick={() => setConfirmDelete({ type: 'vessel', id: v.id, name: v.name })}>Del</Btn>
                    </div>
                  </div>
                ))}
                {/* Add new card */}
                <div onClick={() => openAdd('vessel', { status: 'active' })}
                  style={{
                    border: '2px dashed var(--border2)', borderRadius: 10, padding: 16, display: 'flex',
                    flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    gap: 8, minHeight: 160, transition: 'all .15s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.background = ''; }}>
                  <span style={{ fontSize: 28, color: 'var(--muted)' }}>+</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>Add Vessel</span>
                </div>
              </div>
            ) : (
              <Table headers={['Vessel Name', 'IMO Number', 'Type', 'Active', 'Pending', 'Done', 'Status', 'Actions']}>
                {currentItems.map(v => (
                  <Tr key={v.id}>
                    <Td strong>{v.name}</Td>
                    <Td>{v.imo}</Td>
                    <Td>{v.type}</Td>
                    <Td><span style={{ color: 'var(--success)', fontWeight: 700 }}>{v.activeStock}</span></Td>
                    <Td><span style={{ color: 'var(--warning)', fontWeight: 700 }}>{v.pending}</span></Td>
                    <Td><span style={{ color: 'var(--info)', fontWeight: 700 }}>{v.completed}</span></Td>
                    <Td><Badge status={v.status} /></Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Btn size="xs" variant="outline" onClick={() => openEdit('vessel', v)}>Edit</Btn>
                        <Btn size="xs" variant="danger" onClick={() => setConfirmDelete({ type: 'vessel', id: v.id, name: v.name })}>Delete</Btn>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Table>
            )}
            
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid var(--border)', background: 'var(--white)' }}>
                    <Btn variant="outline" size="sm" onClick={() => setVesselPage(p => Math.max(1, p - 1))} disabled={vesselPage === 1}>Previous</Btn>
                    
                    <div style={{ display: 'flex', gap: 6 }}>
                        {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(page => (
                            <button key={page} onClick={() => setVesselPage(page)}
                                style={{
                                    width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                                    background: vesselPage === page ? 'var(--accent)' : 'transparent',
                                    color: vesselPage === page ? '#fff' : 'var(--text2)',
                                    transition: 'all .15s'
                                }}>
                                {page}
                            </button>
                        ))}
                    </div>
                    
                    <Btn variant="outline" size="sm" onClick={() => setVesselPage(p => Math.min(totalPages, p + 1))} disabled={vesselPage === totalPages}>Next</Btn>
                </div>
            )}
          </Card>
        )
      })()}

      {/* ── SUPPLIERS ── */}
      {tab === 'suppliers' && (() => {
        const filtered = [...suppliers].reverse().filter(s =>
          !search ||
          s.name.toLowerCase().includes(search.toLowerCase()) ||
          s.country.toLowerCase().includes(search.toLowerCase())
        );
        const totalPages = Math.ceil(filtered.length / itemsPerPage);
        const currentItems = filtered.slice((suppPage - 1) * itemsPerPage, suppPage * itemsPerPage);
        return (
          <Card>
            <CardHeader>
              <CardTitle>All Suppliers ({filtered.length})</CardTitle>
              <div style={{ display: 'flex', gap: 12, flex: 1, marginLeft: 20 }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
                  <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }} width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search supplier name, country..." style={{ ...inputBase, paddingLeft: 32, width: '100%' }} />
                </div>
              </div>
              <Btn variant="primary" size="sm" onClick={() => openAdd('supplier', { freightTypes: [] })}>+ Add Supplier</Btn>
            </CardHeader>
            <Table headers={['Supplier Name', 'Country', 'Email', 'Phone', 'Freight Modes', 'Intakes', 'Actions']}>
              {currentItems.map(s => (
                <Tr key={s.id}>
                  <Td strong>{s.name}</Td>
                  <Td>{s.country}</Td>
                  <Td>{s.contact}</Td>
                  <Td>{s.phone}</Td>
                  <Td>{(s.freightTypes || []).map(f => <Chip key={f}>{f}</Chip>)}</Td>
                  <Td>{s.totalIntakes}</Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn size="xs" variant="outline" onClick={() => openEdit('supplier', s)}>Edit</Btn>
                      <Btn size="xs" variant="danger" onClick={() => setConfirmDelete({ type: 'supplier', id: s.id, name: s.name })}>Delete</Btn>
                    </div>
                  </Td>
                </Tr>
              ))}
            </Table>
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid var(--border)', background: 'var(--white)' }}>
                    <Btn variant="outline" size="sm" onClick={() => setSuppPage(p => Math.max(1, p - 1))} disabled={suppPage === 1}>Previous</Btn>
                    
                    <div style={{ display: 'flex', gap: 6 }}>
                        {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(page => (
                            <button key={page} onClick={() => setSuppPage(page)}
                                style={{
                                    width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                                    background: suppPage === page ? 'var(--accent)' : 'transparent',
                                    color: suppPage === page ? '#fff' : 'var(--text2)',
                                    transition: 'all .15s'
                                }}>
                                {page}
                            </button>
                        ))}
                    </div>
                    
                    <Btn variant="outline" size="sm" onClick={() => setSuppPage(p => Math.min(totalPages, p + 1))} disabled={suppPage === totalPages}>Next</Btn>
                </div>
            )}
          </Card>
        )
      })()}

      {/* ── MASTERS ── */}
      {tab === 'masters' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
          <MasterCard title="🛡 Admin & Staff Users" onAdd={() => openAdd('user')}
            headers={['Name', 'Role', '']}>
            {users.filter(u => u.role !== 'customer').map(u => (
              <Tr key={u.id}>
                <Td strong>{u.name}</Td>
                <Td><Chip variant={u.role === 'admin' ? 'primary' : 'success'}>{u.role}</Chip></Td>
                <Td>
                   <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span onClick={() => openEdit('user', u)} style={{ color: 'var(--accent)', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>✎</span>
                      <span onClick={() => setConfirmDelete({ type: 'user', id: u.id, name: u.name })}
                        style={{ color: 'var(--danger)', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>×</span>
                   </div>
                </Td>
              </Tr>
            ))}
            {users.filter(u => u.role !== 'customer').length === 0 && <Tr><Td muted colSpan={3}>No admin/staff users found</Td></Tr>}
          </MasterCard>

          <MasterCard title="🏭 Warehouse Locations" onAdd={() => openAdd('location')}
            headers={['Code', 'Name', '']}>
            {locations.map(l => (
              <Tr key={l.id}><Td strong>{l.code}</Td><Td>{l.name}</Td>
                <Td>
                   <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span onClick={() => openEdit('location', l)} style={{ color: 'var(--accent)', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>✎</span>
                      <span onClick={() => setConfirmDelete({ type: 'location', id: l.id, name: l.name })}
                        style={{ color: 'var(--danger)', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>×</span>
                   </div>
                </Td>
              </Tr>
            ))}
          </MasterCard>

          <MasterCard title="🚛 Freight Types" onAdd={() => openAdd('freight')}
            headers={['Name', 'Mode', '']}>
            {freightTypes.map(f => (
              <Tr key={f.id}><Td strong>{f.name}</Td><Td><Chip>{f.mode}</Chip></Td>
                <Td>
                   <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span onClick={() => openEdit('freight', f)} style={{ color: 'var(--accent)', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>✎</span>
                      <span onClick={() => setConfirmDelete({ type: 'freight', id: f.id, name: f.name })}
                        style={{ color: 'var(--danger)', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>×</span>
                   </div>
                </Td>
              </Tr>
            ))}
          </MasterCard>

          <MasterCard title="🌍 Origin Countries" onAdd={() => openAdd('country')}
            headers={['Country', 'Code', '']}>
            {countries.map(c => (
              <Tr key={c.id}><Td strong>{c.flag} {c.name}</Td><Td>{c.code}</Td>
                <Td>
                   <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span onClick={() => openEdit('country', c)} style={{ color: 'var(--accent)', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>✎</span>
                      <span onClick={() => setConfirmDelete({ type: 'country', id: c.id, name: c.name })}
                        style={{ color: 'var(--danger)', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>×</span>
                   </div>
                </Td>
              </Tr>
            ))}
          </MasterCard>
        </div>
      )}

      {/* ── MODAL ── */}
      {modal && (
        <Modal open onClose={closeM}
          title={`${modal.mode === 'add' ? 'Add' : 'Edit'} ${modalTitles[modal.type] || 'User'}`}
          icon={{ customer: '👥', vessel: '🚢', supplier: '🏭', location: '📍', freight: '🚛', country: '🌍', user: '🛡' }[modal.type]}
          footer={<>
            <Btn variant="outline" onClick={closeM}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave}>{modal.mode === 'add' ? 'Add' : 'Save Changes'}</Btn>
          </>}>

          {formError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', padding: 12, borderRadius: 8, marginBottom: 16, color: '#dc2626', fontSize: 13, fontWeight: 500 }}>
                ⚠️ <b>Error:</b> {formError}
              </div>
          )}

          {modal.type === 'customer' && (
            <FormGrid>
              <FormGroup label="Company Name" required full><Input value={formData.company || ''} onChange={e => set('company', e.target.value)} placeholder="e.g. PT Sumber Jaya" /></FormGroup>
              <FormGroup label="Contact Person" required><Input value={formData.contact || ''} onChange={e => set('contact', e.target.value)} placeholder="Full name" /></FormGroup>
              <FormGroup label="Email" required><Input type="email" value={formData.email || ''} onChange={e => set('email', e.target.value)} placeholder="email@company.com" /></FormGroup>
              <FormGroup label="Phone">                   <Input value={formData.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="+62-..." /></FormGroup>
              <FormGroup label="Status"><CustomSelect value={formData.status || 'active'} onChange={e => set('status', e.target.value)}><option value="active">Active</option><option value="inactive">Inactive</option></CustomSelect></FormGroup>
              {modal.mode === 'add' && (
                <>
                  <FormGroup label="Portal Password" required>
                    <div style={{ position: 'relative' }}>
                      <Input type={showPass ? 'text' : 'password'} value={formData.password || ''} onChange={e => set('password', e.target.value)} placeholder="Min 8 chars" />
                      <span onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', opacity: 0.6 }}>{showPass ? '👁️' : '🕶️'}</span>
                    </div>
                  </FormGroup>
                  <FormGroup label="Repeat Password" required>
                    <Input type={showPass ? 'text' : 'password'} value={formData.confirmPassword || ''} onChange={e => set('confirmPassword', e.target.value)} placeholder="Verify password" />
                  </FormGroup>
                </>
              )}
            </FormGrid>
          )}

          {modal.type === 'vessel' && (
            <FormGrid>
              <FormGroup label="Vessel Name" required full><Input value={formData.name || ''} onChange={e => set('name', e.target.value)} placeholder="e.g. MV Neptune" /></FormGroup>
              <FormGroup label="IMO Number" required><Input value={formData.imo || ''} onChange={e => set('imo', e.target.value)} placeholder="e.g. 9876543" style={{ fontFamily: "'JetBrains Mono',monospace" }} /></FormGroup>
              <FormGroup label="Vessel Type" required>
                <CustomSelect value={formData.type || ''} onChange={e => set('type', e.target.value)}>
                  <option value="">CustomSelect...</option>
                  <option>Bulk Carrier</option><option>Container Ship</option>
                  <option>General Cargo</option><option>Tanker</option>
                </CustomSelect>
              </FormGroup>
              <FormGroup label="Flag / Country">
                <div style={{ position: 'relative' }}>
                  <Input value={formData.flag || ''} onChange={e => { set('flag', e.target.value); }} placeholder="Type country name e.g. Indonesia" />
                  <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 20 }}>{getFlag(formData.flag)}</span>
                </div>
              </FormGroup>
              <FormGroup label="Status"><CustomSelect value={formData.status || 'active'} onChange={e => set('status', e.target.value)}><option value="active">Active</option><option value="inactive">Inactive</option></CustomSelect></FormGroup>
              <FormGroup label="Owned by Customers" full hint="CustomSelect which customer(s) own this vessel">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {customers.map(c => {
                    const sel = (formData.customerIds || []).includes(c.id);
                    return (
                      <label key={c.id} style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6,
                        border: `1.5px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                        background: sel ? 'var(--accent-light)' : 'var(--white)',
                        cursor: 'pointer', fontSize: 12.5, fontWeight: 500, transition: 'all .15s',
                        color: sel ? 'var(--accent)' : 'var(--text2)'
                      }}>
                        <input type="checkbox" checked={sel}
                          onChange={() => {
                            const arr = formData.customerIds || [];
                            set('customerIds', sel ? arr.filter(x => x !== c.id) : [...arr, c.id]);
                          }}
                          style={{ accentColor: 'var(--accent)' }} />
                        {c.company}
                      </label>
                    );
                  })}
                </div>
              </FormGroup>
            </FormGrid>
          )}

          {modal.type === 'supplier' && (
            <FormGrid>
              <FormGroup label="Supplier Name" required full><Input value={formData.name || ''} onChange={e => set('name', e.target.value)} placeholder="e.g. PT Maju Jaya" /></FormGroup>
              <FormGroup label="Country">
                <div style={{ position: 'relative' }}>
                  <Input value={formData.country || ''} onChange={e => set('country', e.target.value)} placeholder="Type country name e.g. Indonesia" />
                  <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 20 }}>{getFlag(formData.country)}</span>
                </div>
              </FormGroup>
              <FormGroup label="Email">          <Input value={formData.contact || ''} onChange={e => set('contact', e.target.value)} placeholder="email@company.com" /></FormGroup>
              <FormGroup label="Phone">         <Input value={formData.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="+62-..." /></FormGroup>
              <FormGroup label="Freight Types" full hint="CustomSelect freight modes this supplier uses (from Dropdown Masters)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {freightTypes.map(f => {
                    const selected = (formData.freightTypeIds || []).includes(f.id);
                    return (
                      <label key={f.id} style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6,
                        border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                        background: selected ? 'var(--accent-light)' : 'var(--white)',
                        cursor: 'pointer', fontSize: 12.5, fontWeight: 500, transition: 'all .15s',
                        color: selected ? 'var(--accent)' : 'var(--text2)'
                      }}>
                        <input type="checkbox" checked={selected}
                          onChange={() => {
                            const arr = formData.freightTypeIds || [];
                            set('freightTypeIds', selected ? arr.filter(x => x !== f.id) : [...arr, f.id]);
                          }}
                          style={{ accentColor: 'var(--accent)' }} />
                        {f.name} <span style={{ fontSize: 10, color: 'var(--muted)' }}>({f.mode})</span>
                      </label>
                    );
                  })}
                </div>
              </FormGroup>
            </FormGrid>
          )}

          {modal.type === 'location' && (
            <FormGrid>
              <FormGroup label="Location Code" required><Input value={formData.code || ''} onChange={e => set('code', e.target.value)} placeholder="WH-C1" style={{ fontFamily: "'JetBrains Mono',monospace" }} /></FormGroup>
              <FormGroup label="Location Name" required><Input value={formData.name || ''} onChange={e => set('name', e.target.value)} placeholder="Block C Section 1" /></FormGroup>
              <FormGroup label="Notes" full>            <Input value={formData.note || ''} onChange={e => set('note', e.target.value)} placeholder="Optional description" /></FormGroup>
            </FormGrid>
          )}

          {modal.type === 'freight' && (
            <FormGrid>
              <FormGroup label="Freight Name" required><Input value={formData.name || ''} onChange={e => set('name', e.target.value)} placeholder="Sea Freight" /></FormGroup>
              <FormGroup label="Mode" required><Input value={formData.mode || ''} onChange={e => set('mode', e.target.value)} placeholder="Ocean" /></FormGroup>
              <FormGroup label="Code">                 <Input value={formData.code || ''} onChange={e => set('code', e.target.value)} placeholder="SEA" style={{ fontFamily: "'JetBrains Mono',monospace" }} /></FormGroup>
            </FormGrid>
          )}

          {modal.type === 'country' && (
            <FormGrid>
              <FormGroup label="Country Name" required>
                <Input value={formData.name || ''} onChange={e => { set('name', e.target.value); set('flag', getFlag(e.target.value)); }} placeholder="Indonesia" />
              </FormGroup>
              <FormGroup label="Code" required><Input value={formData.code || ''} onChange={e => set('code', e.target.value)} placeholder="ID" style={{ fontFamily: "'JetBrains Mono',monospace" }} /></FormGroup>
              <FormGroup label="Flag Emoji">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Input value={formData.flag || ''} onChange={e => set('flag', e.target.value)} placeholder="auto" style={{ flex: 1 }} readOnly />
                  <span style={{ fontSize: 28 }}>{formData.flag || '�'}</span>
                </div>
              </FormGroup>
            </FormGrid>
          )}

          {modal.type === 'user' && (
            <FormGrid>
              <FormGroup label="Full Name" required full><Input value={formData.name || ''} onChange={e => set('name', e.target.value)} placeholder="John Doe" /></FormGroup>
              <FormGroup label="Email" required full><Input type="email" value={formData.email || ''} onChange={e => set('email', e.target.value)} placeholder="admin@cargoflow.com" /></FormGroup>
              <FormGroup label="Password" required={modal.mode === 'add'} full hint={modal.mode === 'edit' ? "Leave blank to keep current password" : ""}>
                <Input type="text" value={formData.password || ''} onChange={e => set('password', e.target.value)} placeholder="Min 8 chars, 1 Uppercase" />
              </FormGroup>
              <FormGroup label="Role" required full>
                <CustomSelect value={formData.role || ''} onChange={e => set('role', e.target.value)}>
                   <option value="">Select Role...</option>
                   <option value="admin">Administrator (Setup)</option>
                   <option value="warehouse">Warehouse Staff</option>
                </CustomSelect>
              </FormGroup>
            </FormGrid>
          )}
        </Modal>
      )}

      {/* Confirm Delete Modal */}
      <Modal open={!!confirmDelete} onClose={() => { setConfirmDelete(null); setDeleteError(''); }}
        title="Confirm Deletion" subtitle="This action cannot be undone."
        icon="🗑" iconBg="#fee2e2"
        footer={<>
          <Btn variant="outline" onClick={() => { setConfirmDelete(null); setDeleteError(''); }}>No, Cancel</Btn>
          {(confirmDelete?.type === 'customer' && customers.find(c => c.id === confirmDelete.id)?.activeStock > 0) ? (
            <Btn variant="danger" disabled title="Cannot delete customer with active stock">Locked</Btn>
          ) : (
            <Btn variant="danger" onClick={handleDelete}>Yes, Delete Permanent</Btn>
          )}
        </>}>
        <div style={{ padding: '10px 0' }}>
          {deleteError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', padding: 12, borderRadius: 8, marginBottom: 12, color: '#dc2626', fontSize: 13, fontWeight: 500 }}>
                ⚠️ <b>Action Blocked:</b> {deleteError}
              </div>
          )}
          {confirmDelete?.type === 'customer' && customers.find(c => c.id === confirmDelete.id)?.activeStock > 0 && (
             <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', padding: 12, borderRadius: 8, marginBottom: 12, color: '#dc2626', fontSize: 13, fontWeight: 500 }}>
               ⚠️ <b>Action Blocked:</b> This customer has <b>{customers.find(c => c.id === confirmDelete.id)?.activeStock} Active Stock</b> entries. You must transfer or complete all stock before you can delete this customer account.
             </div>
          )}
          Are you sure you want to delete <b>{confirmDelete?.name}</b>?
          This will remove it from the database permanently.
        </div>
      </Modal>
    </div>
  );
}

function MasterCard({ title, onAdd, headers, children }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <Btn variant="primary" size="sm" onClick={onAdd}>+ Add</Btn>
      </CardHeader>
      <Table headers={headers}>{children}</Table>
    </Card>
  );
}
